<template>
  <vue-scroll class="login-page align-vertical">
    <div class="form-wrapper align-vertical-middle">
      <div class="form-box card-base card-shadow--medium">
        <img class="image-logo" :src="logoURL" alt="logo" />

        <form @submit.prevent="login">
          <el-input v-model="form.email" placeholder="Enter your email">
            <template slot="prefix">Email</template>
          </el-input>
          <el-input
            type="password"
            v-model="form.password"
            placeholder="Enter your password"
            autocomplete="off"
          >
            <label slot="prefix">Password</label>
          </el-input>
          <div class="flex">
            <div class="box grow text-center">
              <router-link to="/forgot-password">Forgot Password?</router-link>
            </div>
          </div>
          <div class="flex text-center center pt-30 pb-10">
            <el-button
              v-loading="processing"
              native-type="submit"
              class="login-btn"
            >
              Login
            </el-button>
          </div>
        </form>
        <div class="sso">
          <div class="sso__divider">
            <hr />
            <p>or</p>
            <hr />
          </div>
          <div class="flex text-center center pt-10 pb-10">
            <el-button @click="redirectToGoogleSSO" class="sso__btn">
              <v-svg class="sso__btn-icon" sprite="google" />
              Continue with Google
            </el-button>
          </div>
          <div class="flex text-center center pt-10">
            <el-button @click="redirectToMicrosoftSSO" class="sso__btn">
              <v-svg
                class="sso__btn-icon sso__btn-icon--ms"
                sprite="microsoft"
              />
              Continue with Microsoft
            </el-button>
          </div>
        </div>
      </div>
    </div>
  </vue-scroll>
</template>

<script>
import feathersClient, { apiV2Host } from "../../../feathers/feathers-client";
import { mapActions } from "vuex";
import findIndex from "lodash/findKey";
import moment from "moment-timezone";
import store from "@/vuex/store";
import { debounce } from "lodash";

// SSO is a top-level redirect, not a popup: Entra serves COOP: same-origin on
// its sign-in page, which permanently nulls window.opener, so a popup has no way
// to hand its result back. The redirect reloads the app, so the route the user
// was headed to has to outlive Vuex.
const SSO_RETURN_KEY = "sso-return-path";

export default {
  name: "Login",
  data() {
    return {
      processing: false,
      branchOptions: [],
      departmentOptions: [],
      form: {
        email: "",
        password: "",
      },
    };
  },
  computed: {
    user() {
      return this.$store.state.auth.user;
    },
    logoURL() {
      return this.$store.state.branding.logoOnlyURL;
    },
  },
  watch: {
    user() {
      if(this.user){
        this.processLogin();
      }
    },
  },
  methods: {
    ...mapActions("phonebook-entries", { getPhonebookEntries: "find" }),

    async login() {

      // clean user state
      this.$store.commit("setAuthUser", null);

      try {
        this.processing = true;

        const body = {
          email: this.form.email.trim(),
          password: this.form.password.trim(),
        };

        const res = await fetch(`${apiV2Host}/authentication/mfa`, {
          method: "POST",
          body: JSON.stringify(body),
          headers: {
            "Content-Type": "application/json",
          },
        });

        const json = await res.json();
        this.processing = false;

        if (!res.ok) {
          this.$notify({
            title: json.name,
            message: json.message,
            type: "error",
          });
          return;
        }

        const { mfaSetupToken, mfaVerifyToken, accessToken, mode } =
          json;

        if (mfaSetupToken) {
          this.$notify({
            title: "2FA is required!",
            message:
              "Your organization requires Two-Factor Authentication. Please setup Two-Factor Authentication.",
            type: "warning",
          });
          this.$router.push({
            path: "setup-mfa",
            query: { token: mfaSetupToken },
          });
        }

        if (mfaVerifyToken) {
          let message = "";
          if (mode === "sms") {
            message = "A 6-digit code has been sent to your phone.";
          }

          if (mode === "email") {
            message = "A 6-digit code has been sent to your email";
          }

          if (mode === "app") {
            message = "Use the 6-digit code from your authenticator app.";
          }

          this.$notify({
            type: "success",
            title: mode === "app" ? "Authenticator App" : "Check your inbox",
            message,
          });

          this.$router.push({
            path: "validate-mfa",
            query: { token: mfaVerifyToken, mode },
          });
        }

        if (accessToken) {
          await this.$store.dispatch("auth/authenticate", {
            strategy: "jwt",
            accessToken: accessToken,
          });

          this.$store.commit("setLogin");
          // this.$router.push("/omnia/contacts");
        }
      } catch (err) {
        this.processing = false;
        this.$notify({
          title: err.name,
          message: err.message,
          type: "error",
        });
      }
    },

    async processLogin() {
      const { myExtension, userRole, timezone, tenantId, partnerId } =
        this.user;

      //calculate time offset for timezone
      const offsetUTCServer = moment.tz("America/Chicago").utcOffset();
      const offsetMe = moment.tz(timezone).utcOffset();
      const userOffset = offsetMe - offsetUTCServer;
      store.commit("setUserTimeOffset", userOffset);

      if (!myExtension) {
        this.$notify({
          title: "No Extension",
          message: "Your user has no assigned extension. Contact an admin.",
          type: "error",
          duration: 0,
        });

        await new Promise((resolve) => setTimeout(resolve, 100));

        this.$router.push('logout');
      } else {
        //find any tenants for the user
        // const tenants = await this.$store.dispatch("tenants/find");
        // this.$store.commit("setTenants", tenants);

        //if this is a platform admin or partner admin hook to accounts channel real time
        if (+userRole === 1 || +userRole === 11) {
          await feathersClient.service("channel-memberships").create({
            roomType:
              +userRole === 1 ? "platform-accounts" : "partner-accounts",
            partnerId,
          });
        }

        if (myExtension) {
          const {
            dnd,
            status,
            hostServer,
            peerName,
            tenantCode,
            number,
            tenantId,
            omniaPhonebookId,
          } = this.user.myExtension;

          //update user specific info in store
          this.$store.commit("setUserDND", dnd === "on");
          this.$store.commit("setUserStatus", status);
          this.$store.commit("setUserHostServer", hostServer);

          //joins the user specific channel
          await feathersClient.service("channel-memberships").create({
            roomType: "user",
            peerName,
          });

          //send request to get any connected calls
          if (peerName) {
            feathersClient.service("nats-actions-handler").create({
              reqType: "refreshConnectedCalls",
              hostServer,
              peerName,
              tenantCode,
              tenantId,
              userId: this.user.id,
              userExten: number,
              peerNames: [peerName],
            });
          }

          // Load custom contacts
          if (omniaPhonebookId) {
            await this.getPhonebookEntries({
              query: {
                tenantId,
                phonebookId: omniaPhonebookId,
              },
            });
          }
        }

        this.$store.commit("setLogin");

        await new Promise((resolve) => setTimeout(resolve, 1));

        // ⭐️ `Normal Login` user event log ⭐️ //
        this.$store.dispatch("userEvents/create", { event: "Login" });

        // ⭐️ ask location after login ⭐️ //
        this.$store.dispatch("promptCurrentLocation");

        //see if there was an original intended route
        const originalPath = this.$store.state.originalRoute;
        return originalPath
          ? this.$router.push(originalPath)
          : this.$router.push("/omnia/contacts");
      }
    },

    startSSO(url) {
      sessionStorage.setItem(
        SSO_RETURN_KEY,
        this.$store.state.originalRoute || ""
      );

      // a leftover jwt would otherwise be picked up by the router guard on the
      // way back and signed in ahead of the new token
      this.$store.commit("setAuthUser", null);
      this.$store.commit("setLogout");
      localStorage.removeItem("feathers-jwt");

      window.location.assign(url);
    },

    redirectToGoogleSSO() {
      this.startSSO(`${apiV2Host}/oauth/google`);
    },

    redirectToMicrosoftSSO() {
      this.startSSO(`${apiV2Host}/oauth/azure`);
    },

    notifyErrorOnSSO({ title, message }) {
      const debouncedFn = debounce(() => {
        this.$notify({
          title,
          message,
          type: "error",
        });
      }, 500);
      debouncedFn();
    },
  },

  mounted() {

    const { query = {} } = this.$route;
    const { accessToken, error } = query;

    if (accessToken) {
      const returnPath = sessionStorage.getItem(SSO_RETURN_KEY);
      sessionStorage.removeItem(SSO_RETURN_KEY);

      // storage is user-writable, so don't hand an off-site path to the router
      if (returnPath && returnPath.startsWith("/") && !returnPath.startsWith("//")) {
        this.$store.commit("setOriginalRoute", returnPath);
      }

      this.$store
        .dispatch("auth/authenticate", {
          strategy: "jwt",
          accessToken: accessToken,
        })
        .catch((err) => {
          this.notifyErrorOnSSO({
            title: err.name,
            message: err.message,
          });
        });
    }

    if (error && error === "true") {
      this.notifyErrorOnSSO({
        title: "Something Went Wrong!",
        message: "Please try again later!",
      });
    }
  },
};
</script>

<style lang="scss">
@import "../../../assets/scss/_variables";

@media (max-width: 768px) {
  .layout-container .container .view.login-page {
    margin-left: -5px;
    margin-right: -5px;
    max-width: calc(100% + 10px);
  }
}

.sso {
  &__divider {
    display: flex;
    align-items: center;

    hr {
      margin: 0px;
      height: 1px;
      border: none;
      background-color: rgb(225, 225, 225);
      flex: 1 1 0%;
    }

    p {
      font-size: 12px;
      padding: 0 10px;
      color: white;
    }
  }

  &__btn {
    width: 100%;
    position: relative;

    &-icon {
      display: inline-block;
      width: 18px;
      height: 18px;
      stroke-width: 0;
      stroke: currentColor;
      fill: currentColor;

      position: absolute;
      top: 45%;
      left: 16px;
      transform: translateY(-50%);
    }

    &-icon--ms {
      transform: translateY(-50%) scale(1.175);
    }
  }
}
</style>
